import React, { useEffect, useMemo, useState } from 'react';
import { SearchInput } from 'src/ui/SearchInput';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Select } from 'src/ui/Select';
import { Checkbox } from 'src/ui/Checkbox';
import { Modal } from 'src/ui/Modal';
import { Alert } from 'src/ui/Alert';
import { EmptyState } from 'src/ui/EmptyState';
import { LoadingBlock } from 'src/ui/Spinner';
import { Tabs } from 'src/ui/Tabs';
import { VectorOperations } from './operations';
import {
  DuplicatePair,
  LabelStatus,
  MergeActiveReport,
  TriageProposalRow,
  TriageReport,
  useGetLabelDefinitionsQuery,
  useLazyFindDuplicatesQuery,
  useMergeActiveLabelsMutation,
  useTriageApplyMutation,
  useTriageDryRunMutation,
} from 'src/store/vectorProfileApi';

const PAGE_SIZE = 20;

const DOMAIN_COLOR: Record<string, string> = {
  CORE: 'purple',
  LIFESTYLE: 'blue',
  HOBBIES: 'green',
};

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  pending: 'amber',
  merged: 'gray',
};

const ACTION_COLOR: Record<string, string> = {
  merge: 'blue',
  promote: 'green',
  discard: 'red',
};

function DomainBadge({ domain }: { domain: string | null }) {
  if (!domain) return <span className="text-faint">—</span>;
  return (
    <Badge color={DOMAIN_COLOR[domain] ?? 'gray'} size="sm">
      {domain}
    </Badge>
  );
}

function StatusBadge({
  status,
  mergedInto,
}: {
  status: string | null;
  mergedInto: string | null;
}) {
  if (!status) return <span className="text-faint">—</span>;
  return (
    <Badge color={STATUS_COLOR[status] ?? 'gray'} size="sm">
      {status}
      {status === 'merged' && mergedInto ? ` → ${mergedInto}` : ''}
    </Badge>
  );
}

/** What a triage action will do, rendered for the review table. */
function TriageTarget({ row }: { row: TriageProposalRow }) {
  if (row.action === 'merge') {
    return (
      <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
        → {row.into}
      </span>
    );
  }
  if (row.action === 'promote') {
    const canonical = row.canonical_key || row.key;
    return (
      <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
        {canonical !== row.key ? `as ${canonical}` : 'as-is'}
        {row.parent_key ? ` (parent ${row.parent_key})` : ''}
      </span>
    );
  }
  return (
    <span
      style={{ fontSize: 'var(--fs-xs)', color: 'var(--red-solid, #dc2626)' }}
    >
      delete definition
      {row.rowCount > 0
        ? ` + ${row.rowCount} user row${row.rowCount === 1 ? '' : 's'}`
        : ''}
    </span>
  );
}

function TriageModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [runDryRun, dryRun] = useTriageDryRunMutation();
  const [runApply, apply] = useTriageApplyMutation();
  // Keys the reviewer has unchecked (excluded from apply).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [report, setReport] = useState<TriageReport | null>(null);

  useEffect(() => {
    if (open) {
      setExcluded(new Set());
      setReport(null);
      runDryRun();
    }
  }, [open, runDryRun]);

  const proposal = dryRun.data;
  const approved = useMemo(
    () => (proposal?.actions ?? []).filter((a) => !excluded.has(a.key)),
    [proposal, excluded]
  );
  const discardRows = approved
    .filter((a) => a.action === 'discard')
    .reduce((s, a) => s + a.rowCount, 0);

  const toggle = (key: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onApply = async () => {
    const res = await runApply({
      actions: approved.map(({ rowCount, description_current, ...a }) => a),
    });
    if ('data' in res && res.data) setReport(res.data);
  };

  const busy = dryRun.isLoading || apply.isLoading;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title="Triage pending labels"
      subtitle="The LLM proposes an action per pending label. Nothing is written until you approve."
      width={860}
      footer={
        report ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onApply}
              disabled={busy || !proposal || approved.length === 0}
              loading={apply.isLoading}
            >
              Approve & apply{' '}
              {approved.length > 0 ? `(${approved.length})` : ''}
            </Button>
          </>
        )
      }
    >
      {dryRun.isLoading ? (
        <LoadingBlock label="Proposing triage — the LLM is reviewing pending labels…" />
      ) : dryRun.error ? (
        <Alert color="red" title="Dry run failed">
          The triage proposal could not be generated. Check server logs and
          retry.
        </Alert>
      ) : report ? (
        <div className="col" style={{ gap: 12 }}>
          <Alert
            color={report.orphansRemaining > 0 ? 'amber' : 'green'}
            title="Triage applied"
          >
            Promoted {report.upserted} concept{report.upserted === 1 ? '' : 's'}
            , remapped {report.remapped} user label
            {report.remapped === 1 ? '' : 's'}, retired {report.retired} key
            {report.retired === 1 ? '' : 's'}, discarded {report.discarded}{' '}
            definition{report.discarded === 1 ? '' : 's'} (
            {report.discardedRows} user rows deleted).
            {report.orphansRemaining > 0 &&
              ` WARNING: ${report.orphansRemaining} orphaned profile labels remain — investigate.`}
          </Alert>
        </div>
      ) : proposal ? (
        <div className="col" style={{ gap: 14 }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Badge color="amber" size="sm">
              {proposal.summary.pendingTotal} pending
            </Badge>
            <Badge color="blue" size="sm">
              {proposal.summary.merge} merge
            </Badge>
            <Badge color="green" size="sm">
              {proposal.summary.promote} promote
            </Badge>
            <Badge color="red" size="sm">
              {proposal.summary.discard} discard ({proposal.summary.discardRows}{' '}
              user rows)
            </Badge>
          </div>

          {discardRows > 0 && (
            <Alert color="amber" title="Discards delete user data">
              Approved discards will permanently delete {discardRows} profile
              label row{discardRows === 1 ? '' : 's'}. Uncheck any discard you
              are not sure about.
            </Alert>
          )}

          {proposal.actions.length === 0 ? (
            <EmptyState
              icon="check-circle"
              title="Nothing to triage"
              desc="There are no pending labels with a valid proposed action."
            />
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      '',
                      'Pending key',
                      'Rows',
                      'Action',
                      'Result',
                      'Reason',
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: 'left',
                          padding: '7px 10px',
                          fontSize: 'var(--fs-2xs)',
                          fontWeight: 600,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: 'var(--text-muted)',
                          borderBottom: '1px solid var(--border)',
                          background: 'var(--bg-subtle)',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposal.actions.map((row) => {
                    const off = excluded.has(row.key);
                    return (
                      <tr
                        key={row.key}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          opacity: off ? 0.45 : 1,
                        }}
                      >
                        <td style={{ padding: '7px 10px', width: 34 }}>
                          <Checkbox
                            checked={!off}
                            onChange={() => toggle(row.key)}
                          />
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <span
                            className="mono"
                            style={{
                              fontSize: 'var(--fs-xs)',
                              fontWeight: 500,
                              wordBreak: 'break-all',
                            }}
                          >
                            {row.key}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '7px 10px',
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          {row.rowCount}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <Badge
                            color={ACTION_COLOR[row.action] ?? 'gray'}
                            size="sm"
                          >
                            {row.action}
                          </Badge>
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <TriageTarget row={row} />
                        </td>
                        <td
                          style={{
                            padding: '7px 10px',
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          {row.reason ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {proposal.invalid.length > 0 && (
            <Alert
              color="amber"
              title={`${proposal.invalid.length} proposal(s) rejected by validation`}
            >
              <div className="col" style={{ gap: 2 }}>
                {proposal.invalid.map((inv, i) => (
                  <span
                    key={i}
                    className="mono"
                    style={{ fontSize: 'var(--fs-xs)' }}
                  >
                    {inv.action?.key ?? '?'} — {inv.problem}
                  </span>
                ))}
              </div>
            </Alert>
          )}

          {apply.error != null && (
            <Alert color="red" title="Apply failed">
              The triage was not applied. Check server logs and retry.
            </Alert>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

/** One side of a duplicate pair, with enough context to pick the keeper. */
function PairSide({
  label,
  rows,
  desc,
  parent,
}: {
  label: string;
  rows: number;
  desc: string | null;
  parent: string | null;
}) {
  return (
    <div className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
      <span
        className="mono"
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          wordBreak: 'break-all',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
        {rows} user row{rows === 1 ? '' : 's'}
        {parent ? ` · parent ${parent}` : ''}
      </span>
      {desc && (
        <span
          style={{
            fontSize: 'var(--fs-2xs)',
            color: 'var(--text-secondary)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {desc}
        </span>
      )}
    </div>
  );
}

type PairResult = { ok: true; report: MergeActiveReport } | { ok: false };

function DuplicatesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [runScan, scan] = useLazyFindDuplicatesQuery();
  const [runMerge] = useMergeActiveLabelsMutation();
  // Pairs the reviewer unchecked (excluded from apply).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  // Pairs whose keep/retire direction the reviewer swapped.
  const [flipped, setFlipped] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<Record<string, PairResult> | null>(
    null
  );

  useEffect(() => {
    if (open) {
      setExcluded(new Set());
      setFlipped(new Set());
      setResults(null);
      setProgress(0);
      runScan();
    }
  }, [open, runScan]);

  const pairs = scan.data?.data ?? [];
  const pairId = (p: DuplicatePair) => `${p.keyA}|${p.keyB}`;

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  /** Default keeper: more user rows; tie → contract-style lowercase key; tie → shorter key. */
  const defaultKeepA = (p: DuplicatePair) => {
    if (p.rowsA !== p.rowsB) return p.rowsA > p.rowsB;
    const lowA = p.keyA === p.keyA.toLowerCase();
    const lowB = p.keyB === p.keyB.toLowerCase();
    if (lowA !== lowB) return lowA;
    return p.keyA.length <= p.keyB.length;
  };
  const directionOf = (p: DuplicatePair) => {
    const keepA = defaultKeepA(p) !== flipped.has(pairId(p));
    return keepA
      ? { keep: p.keyA, retire: p.keyB, rows: p.rowsB }
      : { keep: p.keyB, retire: p.keyA, rows: p.rowsA };
  };

  const approved = pairs.filter((p) => !excluded.has(pairId(p)));
  const totalRowsMoving = approved.reduce((s, p) => s + directionOf(p).rows, 0);

  const onApply = async () => {
    setApplying(true);
    const out: Record<string, PairResult> = {};
    let i = 0;
    for (const p of approved) {
      i += 1;
      setProgress(i);
      const d = directionOf(p);
      const res = await runMerge({ from: d.retire, into: d.keep });
      out[pairId(p)] =
        'data' in res && res.data
          ? { ok: true, report: res.data }
          : { ok: false };
    }
    setResults(out);
    setApplying(false);
  };

  const succeeded = results
    ? Object.values(results).filter((r) => r.ok).length
    : 0;
  const failed = results ? Object.values(results).length - succeeded : 0;

  return (
    <Modal
      open={open}
      onClose={applying ? undefined : onClose}
      title="Possible duplicate concepts"
      subtitle="Active concepts whose meanings score as near-identical. Similarity is a hint, not proof — opposites like indoor/outdoor also score high. Uncheck pairs that mean different things, then apply."
      width={860}
      footer={
        results ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onApply}
              disabled={applying || approved.length === 0}
              loading={applying}
            >
              Apply {approved.length} merge{approved.length === 1 ? '' : 's'}
              {totalRowsMoving > 0 ? ` (${totalRowsMoving} rows)` : ''}
            </Button>
          </>
        )
      }
    >
      {scan.isFetching ? (
        <LoadingBlock label="Scanning active concepts (first run also builds missing embeddings — may take a minute)…" />
      ) : scan.error ? (
        <Alert color="red" title="Scan failed">
          The duplicate scan did not respond. Check server logs and retry.
        </Alert>
      ) : pairs.length === 0 ? (
        <EmptyState
          icon="check-circle"
          title="No suspicious pairs"
          desc={`No active concept pairs above ${Math.round(
            (scan.data?.threshold ?? 0.85) * 100
          )}% similarity.`}
        />
      ) : (
        <div className="col" style={{ gap: 10 }}>
          {results ? (
            <Alert
              color={failed > 0 ? 'amber' : 'green'}
              title="Merges applied"
            >
              Merged {succeeded} pair{succeeded === 1 ? '' : 's'}
              {failed > 0
                ? `; ${failed} failed — rescan to retry those pairs.`
                : '.'}{' '}
              Retired keys remain visible as “merged →” in the label table.
            </Alert>
          ) : (
            <Alert color="blue" title="Review, then apply">
              Each checked pair will merge the <b>retired</b> key into the{' '}
              <b>kept</b> key: its user labels move over, its children
              re-parent, and it stays recorded as “merged →” (never deleted).
              The keeper is pre-chosen (more user rows, contract-style key) —
              use Swap if it picked wrong, and uncheck pairs that actually mean
              different things.
            </Alert>
          )}
          {applying && (
            <Alert
              color="blue"
              title={`Merging ${progress} of ${approved.length}…`}
            >
              Applying approved merges one at a time. Keep this dialog open.
            </Alert>
          )}
          <div
            className="col"
            style={{ gap: 8, maxHeight: 420, overflowY: 'auto' }}
          >
            {pairs.map((p) => {
              const id = pairId(p);
              const off = excluded.has(id);
              const d = directionOf(p);
              const result = results?.[id];
              return (
                <div
                  key={id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    padding: '10px 12px',
                    opacity: off || result ? 0.6 : 1,
                  }}
                >
                  <div
                    className="row"
                    style={{ gap: 12, alignItems: 'flex-start' }}
                  >
                    {!results && (
                      <Checkbox
                        checked={!off}
                        onChange={() =>
                          setExcluded((prev) => toggleIn(prev, id))
                        }
                      />
                    )}
                    <Badge
                      color={p.similarity >= 0.9 ? 'red' : 'amber'}
                      size="sm"
                    >
                      {(p.similarity * 100).toFixed(1)}%
                    </Badge>
                    <PairSide
                      label={p.keyA}
                      rows={p.rowsA}
                      desc={p.descriptionA}
                      parent={p.parentA}
                    />
                    <PairSide
                      label={p.keyB}
                      rows={p.rowsB}
                      desc={p.descriptionB}
                      parent={p.parentB}
                    />
                  </div>
                  <div
                    className="row"
                    style={{
                      gap: 8,
                      marginTop: 8,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    {result ? (
                      result.ok ? (
                        <span
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--green-solid, #16a34a)',
                          }}
                        >
                          Merged {result.report.from} → {result.report.into} (
                          {result.report.remapped} rows moved,{' '}
                          {result.report.childrenReparented} children
                          re-parented)
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--red-solid, #dc2626)',
                          }}
                        >
                          Merge failed — the pair may have changed. Rescan to
                          retry.
                        </span>
                      )
                    ) : off ? (
                      <span
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Skipped — these stay separate concepts.
                      </span>
                    ) : (
                      <>
                        <span
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          Keep <span className="mono">{d.keep}</span> ← merge{' '}
                          <span className="mono">{d.retire}</span> ({d.rows}{' '}
                          user row{d.rows === 1 ? '' : 's'} move)
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon="arrow-up-down"
                          disabled={applying}
                          onClick={() =>
                            setFlipped((prev) => toggleIn(prev, id))
                          }
                        >
                          Swap
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

const TaxonomyView = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<LabelStatus | ''>('');
  const [domain, setDomain] = useState('');
  const [page, setPage] = useState(1);
  const [triageOpen, setTriageOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);

  // Any filter change restarts from the first page.
  useEffect(() => {
    setPage(1);
  }, [search, status, domain]);

  const { data, isLoading, isFetching, error, refetch } =
    useGetLabelDefinitionsQuery({
      page,
      limit: PAGE_SIZE,
      status,
      domain,
      search,
    });

  const definitions = data?.data ?? [];
  const total = data?.total ?? 0;
  const counts = data?.counts;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = (page - 1) * PAGE_SIZE + definitions.length;

  const statusChips: Array<{
    value: LabelStatus | '';
    label: string;
    n?: number;
  }> = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active', n: counts?.active },
    { value: 'pending', label: 'Pending', n: counts?.pending },
    { value: 'merged', label: 'Merged', n: counts?.merged },
  ];

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Sticky page header */}
      <div
        style={{
          padding: '16px 22px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          background: 'var(--bg-canvas)',
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h1
            className="page-title"
            style={{ fontSize: 'var(--fs-2xl)', marginBottom: 12 }}
          >
            Vector Label Taxonomy
          </h1>
          <div className="row" style={{ gap: 8 }}>
            <Button
              variant="secondary"
              leftIcon="copy"
              disabled={!counts || counts.active === 0}
              onClick={() => setDuplicatesOpen(true)}
            >
              Find duplicates
            </Button>
            <Button
              variant="primary"
              leftIcon="sparkles"
              disabled={!counts || counts.pending === 0}
              onClick={() => setTriageOpen(true)}
            >
              Triage pending{counts ? ` (${counts.pending})` : ''}
            </Button>
          </div>
        </div>
        <div
          className="row"
          style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <div style={{ width: 320 }}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search keys or descriptions…"
              debounce={250}
            />
          </div>
          <div className="row" style={{ gap: 4 }}>
            {statusChips.map((chip) => (
              <Button
                key={chip.value || 'all'}
                variant={status === chip.value ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setStatus(chip.value)}
              >
                {chip.label}
                {chip.n != null ? ` ${chip.n}` : ''}
              </Button>
            ))}
          </div>
          <Select
            size="sm"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            style={{ width: 150 }}
          >
            <option value="">All domains</option>
            <option value="CORE">CORE</option>
            <option value="LIFESTYLE">LIFESTYLE</option>
            <option value="HOBBIES">HOBBIES</option>
          </Select>
        </div>
        <div
          className="text-muted"
          style={{ fontSize: 'var(--fs-sm)', marginTop: 9 }}
        >
          {isLoading ? (
            <span>—</span>
          ) : (
            <span>
              <b style={{ color: 'var(--text-secondary)' }}>
                {pageStart}–{pageEnd}
              </b>{' '}
              of <b style={{ color: 'var(--text-secondary)' }}>{total}</b> label
              definitions
            </span>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div
        className="grow scroll-y"
        style={{
          padding: isLoading || error ? 22 : 0,
          opacity: isFetching && !isLoading ? 0.6 : 1,
        }}
      >
        {isLoading ? (
          <LoadingBlock label="Loading taxonomy…" />
        ) : error ? (
          <div style={{ maxWidth: 520, margin: '40px auto 0' }}>
            <EmptyState
              icon="alert-triangle"
              title="Couldn't load labels"
              desc="The taxonomy service did not respond. Please retry."
              action={
                <Button
                  variant="primary"
                  leftIcon="refresh"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
              }
            />
          </div>
        ) : definitions.length === 0 ? (
          <div style={{ paddingTop: 30 }}>
            <EmptyState
              icon="search"
              title="No labels found"
              desc="Try a different search term or filter."
            />
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            <thead>
              <tr>
                {(
                  [
                    ['Key', '24%'],
                    ['Status', '130px'],
                    ['Parent', '160px'],
                    ['Domain', '120px'],
                    ['Description', 'auto'],
                    ['Created', '110px'],
                  ] as [string, string][]
                ).map(([label, width]) => (
                  <th
                    key={label}
                    style={{
                      textAlign: 'left',
                      padding: '10px 16px',
                      fontSize: 'var(--fs-2xs)',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-subtle)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {definitions.map((def) => (
                <tr
                  key={def.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    opacity: def.status === 'merged' ? 0.5 : 1,
                  }}
                >
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text)',
                        fontWeight: 500,
                        wordBreak: 'break-all',
                      }}
                    >
                      {def.key}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <StatusBadge
                      status={def.status}
                      mergedInto={def.merged_into}
                    />
                  </td>
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {def.parent_key ?? '—'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <DomainBadge domain={def.domain} />
                  </td>
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <div
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {def.description ?? '—'}
                    </div>
                  </td>
                  <td style={{ padding: '11px 16px', verticalAlign: 'top' }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {def.createdAt
                        ? new Date(def.createdAt).toLocaleDateString()
                        : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination footer */}
      {!isLoading && !error && total > 0 && (
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px',
            borderTop: '1px solid var(--border)',
            flexShrink: 0,
            fontSize: 'var(--fs-xs)',
          }}
        >
          <span className="text-muted">
            Page {page} of {totalPages}
          </span>
          <div className="row gap-1">
            <Button
              variant="ghost"
              size="sm"
              leftIcon="chevron-left"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="ghost"
              size="sm"
              rightIcon="chevron-right"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <TriageModal open={triageOpen} onClose={() => setTriageOpen(false)} />
      <DuplicatesModal
        open={duplicatesOpen}
        onClose={() => setDuplicatesOpen(false)}
      />
    </div>
  );
};

const TABS = [
  { id: 'taxonomy', label: 'Taxonomy', icon: 'hash' as const },
  { id: 'operations', label: 'Operations', icon: 'activity' as const },
];

/** Vector section: the label taxonomy, and the rollout steps that change it. */
const VectorLabels = () => {
  const [tab, setTab] = useState('taxonomy');
  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <div
        style={{
          padding: '8px 22px 0',
          flexShrink: 0,
          background: 'var(--bg-canvas)',
        }}
      >
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
      </div>
      <div className="col grow" style={{ minHeight: 0 }}>
        {tab === 'taxonomy' ? <TaxonomyView /> : <VectorOperations />}
      </div>
    </div>
  );
};

export default VectorLabels;
